import { useMemo } from 'react';
import {
  formatCurrency,
  formatDate,
  formatList,
  formatNumber,
  type AnyLocaleType,
} from '@featit/shared/i18n/core';
import { useLocale } from './LocaleProvider';

export interface IFormat {
  locale: AnyLocaleType;
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  currency: (value: number, currency: string, options?: Intl.NumberFormatOptions) => string;
  date: (value: Date | number | string, options?: Intl.DateTimeFormatOptions) => string;
  list: (items: readonly string[], options?: Intl.ListFormatOptions) => string;
}

/**
 * Numbers, money, dates and lists in the language of the open page (IN-6). A component asks for these and never
 * for `toLocaleString('en')` or a bare `Intl.NumberFormat`: `scripts/i18n-formats.mjs` fails a literal language tag,
 * and lets a file's no-locale calls only fall. Outside a component, take `useLocale()`'s value to the helper in
 * `@featit/shared/i18n/core` that this wraps.
 */
export function useFormat(): IFormat {
  const locale = useLocale();
  return useMemo(
    () => ({
      locale,
      number: (value, options) => formatNumber(locale, value, options),
      currency: (value, currency, options) => formatCurrency(locale, value, currency, options),
      date: (value, options) => formatDate(locale, value, options),
      list: (items, options) => formatList(locale, items, options),
    }),
    [locale],
  );
}
